/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import applyIMS from './ims-loader';
const PREFIX = 'https://static.adobelogin.com/imslib';

/* eslint-disable sort-keys */
const ENVS = {
  test: 'stg1',
  dev: 'stg1',
  qa: 'stg1',
  stage: 'stg1',
  prod: ''
};
/* eslint-enable sort-keys */

export const loadScript = (src, env) => {
  // If we are on prod, we likely already have the IMS script
  // in the bundle, so we should activate it.
  if (env === 'prod') {
    applyIMS();
    return Promise.resolve();
  }

  // Otherwise, load it from a script src
  let s = document.createElement('script');
  s.async = true;
  s.src = src;

  return new Promise((resolve, reject) => {
    s.onload = s.onreadystatechange = () => {
      if (!s.readyState || /loaded|complete/.test(s.readyState)) {
        s.onload = s.onreadystatechange = null;
        s = undefined;
        resolve();
      }
    };

    s.onerror = () => reject();
    document.body.appendChild(s);
  });
};

/**
 * Get Best Locale Setting using User Preferences and Supported Locales.
 * @param {Object} profile JSON Object for User's IMS Profile.
 * @param {Array.<string>} supportedLanguages List of Supported Locales.
 * @returns {string} Best Suitable Locale.
 */
export const getBestSupportedLocale = (profile, supportedLanguages) => {
  const preferredLanguages = profile.preferred_languages;
  const firstLocale = preferredLanguages && preferredLanguages.length > 0 ? preferredLanguages[0] : 'en-US';
  const secondLocale = preferredLanguages && preferredLanguages.length > 1 ? preferredLanguages[1] : 'en-US';
  const firstLocalePrefix = firstLocale.split('-')[0];
  const secondLocalePrefix = secondLocale.split('-')[0];
  let locale = supportedLanguages.find((lang) => lang.toUpperCase() === firstLocale.toUpperCase());
  if (!locale && (firstLocalePrefix !== secondLocalePrefix)) {
    locale = supportedLanguages.find((lang) => lang.split('-')[0] === firstLocalePrefix);
  }
  if (!locale) {
    locale = supportedLanguages.find((lang) => lang.toUpperCase() === secondLocale.toUpperCase())
      || supportedLanguages.find((lang) => lang.split('-')[0] === secondLocalePrefix);
  }
  return locale || 'en-US';
};

export const load = (env, config) => {
  let envModifier = ENVS[env];
  let parts = [PREFIX, 'imslib.min.js'];
  if (envModifier) {
    parts.splice(1, 0, envModifier);
  }
  let url = parts.join('/');
  let resolved = false;

  return new Promise(resolve => {
    const handler = () => {
      if (!resolved) {
        resolve();
        resolved = true;
      }
    };
    window.adobeid = Object.assign({}, config, {
      // onProfile fires with fastEvents, whereas onReady does not
      // We provide onReady just in case..
      onProfile: handler,
      onReady: handler,
      optimizations: {fastEvents: true}
    });
    loadScript(url, env);
  });
};

export const getOrgs = async (token, client_id, env) => {
  let res = await fetch(
    `https://adobeid-na1${
      /^(prod)$/.test(env) ? '' : '-' + ENVS[env]
    }.services.adobe.com/ims/organizations/v5?client_id=${client_id}`,
    {
      headers: {Authorization: `Bearer ${token}`}
    }
  );

  let orgs = await res.json();
  return orgs || [];
};

export const saveOrgsToLocalStorage = (env, orgsData) => {
  let orgsArr = orgsData.map(org => ({orgName: org.orgName, orgRef: org.orgRef}));
  localStorage.setItem('orgsArr', JSON.stringify({[env]: orgsArr}));
  return orgsArr;
};

export const getSession = async (sessionId, token, clientId) => {
  let res = await fetch(`${sessionId}?client_id=${clientId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    method: 'GET'
  });
  let data = await res.json();
  return data.session_data || {};
};

export const setSession = async (sessionId, sessionData, token, clientId) => {
  return await fetch(`${sessionId}?client_id=${clientId}`, {
    body: JSON.stringify(sessionData),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    method: 'POST'
  });
};
